import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Award, Users, Building2, Clock, ChevronRight, Shield, HeartPulse, Stethoscope } from "lucide-react";

const stats = [
  { value: "15+", label: "Years of Excellence" },
  { value: "10K+", label: "Procedures Performed" },
  { value: "100%", label: "Safety Record" },
  { value: "24/7", label: "Anesthesia Coverage" },
];

const team = [
  {
    name: "Dr. Michael Reynolds",
    role: "Medical Director",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&h=500&fit=crop",
    specialty: "Board-Certified Anesthesiologist, Fellowship in Cardiac Anesthesia",
  },
  {
    name: "Dr. Sarah Chen",
    role: "Chief of Anesthesiology",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&h=500&fit=crop",
    specialty: "Board-Certified Anesthesiologist, Pediatric Specialty",
  },
  {
    name: "Dr. James Morrison",
    role: "Senior Anesthesiologist",
    image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&h=500&fit=crop",
    specialty: "Board-Certified Anesthesiologist, Pain Management Fellowship",
  },
];

const values = [
  {
    icon: Shield,
    title: "Patient Safety",
    description: "Rigorous protocols and continuous monitoring ensure the highest standards of patient safety.",
  },
  {
    icon: HeartPulse,
    title: "Clinical Excellence",
    description: "Board-certified physicians with extensive training in all aspects of anesthesia care.",
  },
  {
    icon: Stethoscope,
    title: "Personalized Care",
    description: "Every patient receives a customized anesthesia plan tailored to their specific needs.",
  },
  {
    icon: Clock,
    title: "Availability",
    description: "Full anesthesia coverage for scheduled procedures and emergency support when needed.",
  },
];

const certifications = [
  "AAAHC Accredited Facility",
  "Board-Certified Anesthesiologists",
  "Advanced Cardiac Life Support (ACLS)",
  "Pediatric Advanced Life Support (PALS)",
  "HIPAA Compliant",
  "NPI: 1952213472"
];

export default function About() {
  return (
    <div data-testid="about-page" className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 30% 50%, rgba(0, 240, 255, 0.1) 0%, transparent 50%)'
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">About Us</p>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none font-bold italic text-white mb-8">
                Surgery
                <br />
                <span className="gradient-text">Center LLC</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Surgery Center LLC is a premier surgical care center providing expert medical services 
                across our facilities in Atlanta, GA and Newark, DE. We specialize in delivering safe, 
                comfortable care for a wide range of surgical procedures.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                Our team of board-certified physicians brings decades of combined experience 
                to every procedure. We are committed to providing personalized care, utilizing 
                the latest technology, and maintaining the highest safety standards 
                in surgical medicine.
              </p>
              <Link to="/services">
                <Button
                  data-testid="about-services-btn"
                  className="rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-8 py-4 shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all duration-300"
                >
                  Our Services
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=600&h=750&fit=crop"
                  alt="Surgery Center Operating Room"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 border-2 border-neon-pink/30 rounded-2xl" />
              <div className="absolute -top-6 -right-6 w-24 h-24 border-2 border-neon-cyan/30 rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-vip-paper border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} data-testid={`stat-${index}`} className="text-center">
                <p className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold gradient-text mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Our Commitment</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={value.title}
                data-testid={`value-card-${index}`}
                className="vip-card glass rounded-2xl p-8 border border-white/10 hover:border-neon-cyan/30 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neon-cyan/20 to-neon-pink/20 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-neon-cyan" />
                </div>
                <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section data-testid="team-section" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Our Physicians</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Board-Certified Anesthesiologists
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={member.name}
                data-testid={`team-member-${index}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  />
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-vip-black via-transparent to-transparent" />
                {/* Info */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-heading text-xl text-white mb-1">{member.name}</h3>
                  <p className="text-neon-cyan text-sm mb-1">{member.role}</p>
                  <p className="text-gray-400 text-xs">{member.specialty}</p>
                </div>
                {/* Hover border glow */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-neon-pink/50 rounded-2xl transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section data-testid="certifications-section" className="py-24 md:py-32 bg-vip-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-neon-pink uppercase tracking-[0.2em] text-sm mb-4">Accreditations</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white">
              Certifications & Compliance
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {certifications.map((cert, index) => (
              <div
                key={cert}
                className="glass rounded-xl p-6 border border-white/10 text-center"
              >
                <Award className="w-8 h-8 text-neon-cyan mx-auto mb-3" />
                {cert.startsWith("NPI:") ? (
                  <a 
                    href="https://npiregistry.cms.hhs.gov/provider-view/1952213472" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white text-sm font-medium hover:text-neon-cyan transition-colors"
                  >
                    {cert}
                  </a>
                ) : (
                  <p className="text-white text-sm font-medium">{cert}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facility Section */}
      <section data-testid="facility-section" className="py-24 md:py-32 bg-vip-paper">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-neon-cyan uppercase tracking-[0.2em] text-sm mb-4">Our Facility</p>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-8">
                State-of-the-Art
                <br />
                <span className="gradient-text">Surgery Center</span>
              </h2>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                With locations in Atlanta and Newark, our fully accredited surgical care 
                centers feature the latest in surgical technology.
              </p>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-neon-cyan rounded-full" />
                  Advanced patient monitoring systems
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-neon-pink rounded-full" />
                  Modern operating suites
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-royal-purple rounded-full" />
                  Comfortable recovery area
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-neon-cyan rounded-full" />
                  Emergency response capabilities
                </li>
              </ul>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=400&h=400&fit=crop"
                    alt="Operating Room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=400&h=300&fit=crop"
                    alt="Medical Equipment"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1666214280557-f1b5022eb634?w=400&h=300&fit=crop"
                    alt="Recovery Room"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1581595220892-b0739db3ba8c?w=400&h=400&fit=crop"
                    alt="Patient Care"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-vip-black relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(191, 64, 191, 0.15) 0%, transparent 60%)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold italic text-white mb-6">
            Questions About Your <span className="gradient-text">Procedure</span>?
          </h2>
          <p className="text-gray-300 text-base md:text-lg mb-10">
            Schedule a pre-operative consultation with our anesthesia team.
          </p>
          <Link to="/contact">
            <Button
              data-testid="about-contact-btn"
              className="shimmer-btn rounded-full bg-white text-black hover:bg-neon-cyan hover:text-black font-bold uppercase tracking-widest px-10 py-6 text-base shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all duration-300"
            >
              Contact Us
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
